<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    dismissable-mask
    class="help-modal"
    :style="{ width: screen.isMobile ? '100%' : '640px' }"
  >
    <template #header>
      <div class="help-modal-header">
        <button
          v-if="activeKey"
          class="help-back-btn"
          title="Volver al índice"
          data-e2e="help-back-index"
          @click="activeKey = null"
        >
          <i class="pi pi-arrow-left"></i>
        </button>
        <i :class="activeEntry ? activeEntry.icon : 'pi pi-question-circle'" class="help-header-icon"></i>
        <span class="help-header-title">{{ activeEntry ? activeEntry.title : "Centro de ayuda" }}</span>
      </div>
    </template>

    <!-- Índice de secciones -->
    <div v-if="!activeEntry" class="help-index">
      <p class="help-index-intro">
        Elige una sección para ver su ayuda, o busca por palabra clave.
      </p>

      <span class="p-input-icon-left help-search">
        <i class="pi pi-search"></i>
        <InputText v-model="search" placeholder="Buscar ayuda…" class="w-full" />
      </span>

      <template v-for="group in filteredGroups" :key="group.label">
        <div class="help-group-title">{{ group.label }}</div>
        <button
          v-for="item in group.items"
          :key="item.key"
          class="help-index-item"
          :data-e2e="`help-item-${item.key}`"
          @click="activeKey = item.key"
        >
          <i :class="item.icon"></i>
          <div class="help-index-text">
            <span>{{ item.title }}</span>
            <div class="text-xs" v-if="item.summary">{{ item.summary }}</div>
          </div>
          <i class="pi pi-angle-right help-index-chevron"></i>
        </button>
      </template>

      <div v-if="filteredGroups.length === 0" class="help-empty">
        No se encontró ayuda para "{{ search }}".
      </div>
    </div>

    <!-- Detalle de una sección -->
    <div v-else class="help-detail">
      <p class="help-detail-summary" v-if="activeEntry.summary">{{ activeEntry.summary }}</p>

      <div v-for="(section, idx) in activeEntry.sections" :key="idx" class="help-section">
        <h4>{{ section.heading }}</h4>
        <p v-for="(paragraph, i) in section.body || []" :key="i">{{ paragraph }}</p>
        <ul v-if="section.list">
          <li v-for="(item, i) in section.list" :key="i">{{ item }}</li>
        </ul>
      </div>

      <button class="help-back-link" data-e2e="help-back-index-bottom" @click="activeKey = null">
        <i class="pi pi-arrow-left"></i>
        Volver al índice
      </button>
    </div>

    <template #footer>
      <Button label="Cerrar" text @click="emit('update:visible', false)" />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { useRouterStore } from "@/store/router";
import { useScreenStore } from "@/store/useScreenStore";
import { HELP_CONTENT, HELP_GROUPS } from "@/constants/helpContent";

const props = defineProps({
  visible: Boolean,
});
const emit = defineEmits(["update:visible"]);

const route = useRouterStore();
const screen = useScreenStore();

// null => se muestra el índice; una key de HELP_CONTENT => se muestra su detalle.
const activeKey = ref(null);
const search = ref("");

const activeEntry = computed(() => (activeKey.value ? HELP_CONTENT[activeKey.value] : null));

// Al abrir el modal, se muestra directamente la ayuda de la sección actual
// (si existe); si no hay ayuda para esa página, se cae al índice general.
watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return;
    activeKey.value = HELP_CONTENT[route.page] ? route.page : null;
    search.value = "";
  }
);

// El índice usa 'parametros' como key canónica; 'downloadTerritorio' es un
// alias al mismo objeto de contenido y se filtra aquí para no duplicar la fila.
const allEntries = computed(() => {
  const seen = new Set();
  return Object.entries(HELP_CONTENT)
    .filter(([, entry]) => {
      if (seen.has(entry)) return false;
      seen.add(entry);
      return true;
    })
    .map(([key, entry]) => ({ key, ...entry }));
});

const filteredGroups = computed(() => {
  const term = search.value.trim().toLowerCase();
  return HELP_GROUPS.map((label) => ({
    label,
    items: allEntries.value.filter((item) => {
      if (item.group !== label) return false;
      if (!term) return true;
      return item.title.toLowerCase().includes(term) || (item.summary || "").toLowerCase().includes(term);
    }),
  })).filter((group) => group.items.length > 0);
});
</script>

<style scoped>
.help-modal-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.help-back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  color: var(--label-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color var(--duration-fast) var(--ease-standard);
}
.help-back-btn:hover {
  background: var(--fill-quaternary);
}

.help-header-icon {
  color: var(--accent);
  font-size: 1.1rem;
}

.help-header-title {
  font-size: var(--text-title-2);
  font-weight: var(--weight-semibold);
  color: var(--label-primary);
}

.help-index {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.help-index-intro {
  color: var(--label-secondary);
  margin: 0 0 var(--space-2);
}

.help-search {
  position: relative;
  margin-bottom: var(--space-3);
}
.help-search i {
  position: absolute;
  left: var(--space-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--label-tertiary, var(--label-secondary));
}
.help-search :deep(input) {
  width: 100%;
  padding-left: calc(var(--space-3) * 2 + 12px);
}

.help-group-title {
  padding: var(--space-2) var(--space-2) 2px;
  margin-top: var(--space-2);
  font-size: var(--text-caption);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--label-tertiary, var(--label-secondary));
}
.help-group-title:first-child {
  margin-top: 0;
}

.help-index-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2);
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--label-primary);
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color var(--duration-fast) var(--ease-standard);
}
.help-index-item:hover {
  background: var(--fill-quaternary);
}
.help-index-item > i:first-child {
  font-size: 1.05rem;
  width: 18px;
  text-align: center;
  color: var(--label-secondary);
}
.help-index-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.help-index-text > span {
  font-size: var(--text-callout);
  font-weight: var(--weight-semibold);
}
.help-index-text > .text-xs {
  color: var(--label-secondary);
  font-size: var(--text-caption);
}
.help-index-chevron {
  color: var(--label-tertiary, var(--label-secondary));
}

.help-empty {
  padding: var(--space-4) var(--space-2);
  text-align: center;
  color: var(--label-secondary);
}

.help-detail-summary {
  color: var(--label-secondary);
  margin-top: 0;
}

.help-section {
  margin-top: var(--space-4);
}
.help-section:first-of-type {
  margin-top: var(--space-2);
}
.help-section h4 {
  margin: 0 0 var(--space-2);
  font-size: var(--text-callout);
  font-weight: var(--weight-semibold);
  color: var(--label-primary);
}
.help-section p {
  margin: 0 0 var(--space-2);
  color: var(--label-primary);
  line-height: 1.5;
}
.help-section ul {
  margin: 0;
  padding-left: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.help-section li {
  color: var(--label-primary);
  line-height: 1.5;
}

.help-back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: var(--space-4);
  padding: var(--space-2) var(--space-3);
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--accent);
  font-weight: var(--weight-semibold);
  cursor: pointer;
}
.help-back-link:hover {
  background: var(--accent-soft);
}
</style>
